// Calculation engine for the Reports "Command Center" dashboard.
//
// Every number the Command Center shows is computed here as a pure function so
// the math is testable and can't silently drift from the rest of the app.
// Field conventions:
//   - volume            → deal.offerPrice
//   - gross commission  → deal.totalCommissionamount  (the FULL commission the
//        team makes on the deal, before any splits/payouts — per Henry, "gross
//        is how much the team makes total"; the app's Commission Breakdown
//        screen labels this exact field "Total Commission").
//        NOTE: the older Year Over Year report instead sums
//        takehomecommissionamount (what's left AFTER referral/co-op/CBR
//        payouts), so the two reports will differ until Year Over Year is
//        aligned to this definition.
//   - a "closed" deal   → dealStage === "Closed" with a closingdate
// Per-agent credit uses the shared dealCredit helpers so a deal is never
// double-counted across an agent and a team lead.

import { isCountableDeal, dealCreditsAgent } from "./dealCredit";

const num = (v) => {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n : 0;
};

const sum = (list, pick) => list.reduce((total, item) => total + pick(item), 0);

// Which deal field represents "gross commission" — how much the team makes in
// total on a deal. Single-sourced so the whole dashboard re-points in one edit
// if the definition ever changes. See the header note for the Year Over Year
// caveat.
const GROSS_COMMISSION_FIELD = "totalCommissionamount";

export function agentName(user) {
  if (!user) return "Unknown";
  const full = `${user.firstname || ""} ${user.lastname || ""}`.trim();
  return full || user.name || user.email || "Unknown";
}

// A deal's lead source can be stored as a plain string (legacy) OR as the full
// system lead-source object ({id, name, abbreviation, ...}). Always resolve it
// to a display string, so it can be grouped, filtered and — crucially —
// rendered as a React child without crashing.
export function sourceLabel(leadsource) {
  if (!leadsource) return "Unspecified";
  if (typeof leadsource === "string") return leadsource;
  if (typeof leadsource === "object") {
    return (
      leadsource.name ||
      leadsource.abbreviation ||
      leadsource.id ||
      "Unspecified"
    );
  }
  return String(leadsource);
}

// Who belongs in the "agent" filter dropdown. Follows the same conventions as
// utils/roles.js: type 1 = deleted, type 2 = disabled, isDemo = the preview
// account. Only the two deal-carrying roles count as agents — Admin (Team Lead)
// and Employee (Agent) — which keeps clients, Client Care and Accountants out.
export function isActiveAgent(user) {
  if (!user) return false;
  if (user.type === 1 || user.type === 2) return false;
  if (user.isDemo === true) return false;
  return user.role === "Admin" || user.role === "Employee";
}

// Options for the agent filter: every active agent, plus anyone who actually
// produced in the period (so the filter can never hide someone who is sitting
// on the leaderboard, e.g. an agent who has since left).
export function agentFilterOptions(users, producedIds = []) {
  const produced = new Set(producedIds);
  return (users || [])
    .filter((u) => isActiveAgent(u) || produced.has(u.id))
    .map((u) => ({ id: u.id, name: agentName(u) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ── Time ranges ──────────────────────────────────────────────────────────────
// A "period" is one of: ytd (default), mtd, qtd, year, custom. Every range is
// inclusive of both ends. `now` is injectable so tests are deterministic.
// Parses a plain "YYYY-MM-DD" as a LOCAL date. `new Date("2026-03-01")` is
// parsed as UTC midnight, which is the previous evening in US timezones — that
// would push a closing into the wrong day and month, and would silently drop
// deals closing on the first day of a custom range.
export function parseLocalDate(value) {
  if (value instanceof Date) return new Date(value);
  if (typeof value === "string") {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return new Date(value);
}

export function periodRange(period, now = new Date(), custom = null) {
  const y = now.getFullYear();
  const m = now.getMonth();
  const endOfToday = new Date(y, m, now.getDate(), 23, 59, 59, 999);

  switch (period) {
    case "mtd":
      return { start: new Date(y, m, 1), end: endOfToday };
    case "qtd": {
      const q = Math.floor(m / 3) * 3;
      return { start: new Date(y, q, 1), end: endOfToday };
    }
    case "year":
      return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59, 999) };
    case "custom": {
      const from = custom?.start
        ? parseLocalDate(custom.start)
        : new Date(y, 0, 1);
      const to = custom?.end ? parseLocalDate(custom.end) : endOfToday;
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      return { start: from, end: to };
    }
    case "ytd":
    default:
      return { start: new Date(y, 0, 1), end: endOfToday };
  }
}

// The same calendar window shifted back exactly one year, for like-for-like
// year-over-year comparisons ("YTD this year vs YTD last year").
export function priorYearRange(range) {
  const start = new Date(range.start);
  start.setFullYear(start.getFullYear() - 1);
  const end = new Date(range.end);
  end.setFullYear(end.getFullYear() - 1);
  return { start, end };
}

export function inRange(dateStr, range) {
  if (!dateStr || !range) return false;
  const d = parseLocalDate(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  return d >= range.start && d <= range.end;
}

// ── Filtering ────────────────────────────────────────────────────────────────
// Applies the non-time slices (agent, deal type, lead source) and drops dead
// (cancelled / expired) deals. Time filtering is separate because closed
// metrics filter on closingdate while the live pipeline is a "right now"
// snapshot that ignores the date range.
export function applyDealFilters(deals, filters = {}) {
  const { agentId = "all", dealType = "all", source = "all" } = filters;
  return (deals || []).filter((deal) => {
    if (!isCountableDeal(deal)) return false;
    if (dealType !== "all" && deal.dealtype !== dealType) return false;
    if (source !== "all" && sourceLabel(deal.leadsource) !== source) return false;
    if (agentId !== "all" && !dealCreditsAgent(deal, agentId)) return false;
    return true;
  });
}

export function closedDealsInRange(deals, range) {
  return (deals || []).filter(
    (deal) => deal.dealStage === "Closed" && inRange(deal.closingdate, range),
  );
}

// ── Headline metrics ─────────────────────────────────────────────────────────
export function summarize(closed) {
  const list = closed || [];
  const volume = sum(list, (d) => num(d.offerPrice));
  const grossCommission = sum(list, (d) => num(d[GROSS_COMMISSION_FIELD]));
  const units = list.length;
  return {
    volume,
    grossCommission,
    units,
    avgSalePrice: units > 0 ? volume / units : 0,
  };
}

export function percentChange(current, previous) {
  if (previous > 0) return (current / previous) * 100 - 100;
  if (current > 0) return 100;
  return 0;
}

// ── Live pipeline (a "right now" snapshot, independent of the date range) ─────
const OPEN_VALUE_STAGES = ["Under Contract", "Pending"];

export function pipelineSnapshot(deals) {
  const list = deals || [];
  const countStage = (stage) => list.filter((d) => d.dealStage === stage).length;
  return {
    active: countStage("Active"),
    underContract: countStage("Under Contract"),
    pending: countStage("Pending"),
    // Dollars still expected to come in from deals under contract or pending.
    pipelineValue: sum(
      list.filter((d) => OPEN_VALUE_STAGES.includes(d.dealStage)),
      (d) => num(d.offerPrice),
    ),
  };
}

// A funnel of counts: current open pipeline plus what actually closed in the
// selected period. Mixing "live open" with "closed this period" is intentional
// — it reads as "here's the book of business and what landed."
export function funnel(filteredDeals, closedInPeriod) {
  const snap = pipelineSnapshot(filteredDeals);
  return [
    { key: "active", label: "Active", count: snap.active },
    { key: "underContract", label: "Under contract", count: snap.underContract },
    { key: "pending", label: "Pending", count: snap.pending },
    { key: "closed", label: "Closed", count: (closedInPeriod || []).length },
  ];
}

// ── Monthly trend ────────────────────────────────────────────────────────────
// 12 months of gross commission for one calendar year. Feed it the already
// agent/type/source-filtered deals; it does its own year + closed filtering.
export function monthlyCommission(filteredDeals, year) {
  const months = new Array(12).fill(0);
  (filteredDeals || []).forEach((deal) => {
    if (deal.dealStage !== "Closed" || !deal.closingdate) return;
    const dt = parseLocalDate(deal.closingdate);
    if (Number.isNaN(dt.getTime()) || dt.getFullYear() !== year) return;
    months[dt.getMonth()] += num(deal[GROSS_COMMISSION_FIELD]);
  });
  return months;
}

// ── Lead-source mix (of closed gross commission) ─────────────────────────────
export function sourceMix(closed) {
  const map = new Map();
  (closed || []).forEach((deal) => {
    const key = sourceLabel(deal.leadsource);
    map.set(key, (map.get(key) || 0) + num(deal[GROSS_COMMISSION_FIELD]));
  });
  const total = Array.from(map.values()).reduce((a, b) => a + b, 0);
  return Array.from(map.entries())
    .map(([source, amount]) => ({
      source,
      amount,
      pct: total > 0 ? (amount / total) * 100 : 0,
    }))
    .sort((a, b) => b.amount - a.amount);
}

// Keep the mix readable: show the biggest `limit` sources and roll everything
// else into a single "Other" slice, so the chart never needs more colours than
// the palette has (which made different sources share a colour).
export function topSources(mix, limit = 6) {
  const list = mix || [];
  if (list.length <= limit) return list;
  const rest = list.slice(limit);
  return [
    ...list.slice(0, limit),
    {
      source: "Other",
      amount: rest.reduce((total, r) => total + r.amount, 0),
      pct: rest.reduce((total, r) => total + r.pct, 0),
      isOther: true,
    },
  ];
}

// ── Agent leaderboard ────────────────────────────────────────────────────────
// Every column here is about the deals this agent is CREDITED with — one agent
// per deal (see utils/dealCredit.js), so the team never reads as having closed
// the same house twice:
//   - units and volume: how many of those deals, and their sale prices.
//   - gci: GROSS commission on those same deals, i.e. what the team made in
//     total, the identical definition used by summarize() for the "Gross
//     commission" headline above the board and by Henry's yearly goals. Keeping
//     one definition on one screen is deliberate: pctToGoal divides this figure
//     by a goal that was always set in gross dollars, so switching gci to a
//     personal after-split slice would understate every agent by their split.
//     What each person was personally PAID is a different question with a
//     different home: the Team Roster (utils/teamRoster.js), which uses
//     commissionForAgent so a referral fee is never lost.
//
//     Because the two boards answer different questions, they can rank the
//     same two people in opposite orders, and that is expected rather than a
//     bug. Neither calculation changes. The screens carry the difference in
//     their labels instead: this one is headed "Gross commission (whole deal)"
//     in components/reports/command-center.jsx, the roster is headed
//     "Commission Earned" in commission-approvals/TeamRosterPanel.jsx. Rename
//     either one and rename the other with it.
export function leaderboard(closed, users, year) {
  const list = closed || [];
  return (users || [])
    .map((user) => {
      const theirs = list.filter((deal) => dealCreditsAgent(deal, user.id));
      const gci = sum(theirs, (d) => num(d[GROSS_COMMISSION_FIELD]));
      const goal = goalForUser(user, year, "amount");
      return {
        id: user.id,
        name: agentName(user),
        gci,
        units: theirs.length,
        volume: sum(theirs, (d) => num(d.offerPrice)),
        goal,
        pctToGoal: goal > 0 ? (gci / goal) * 100 : null,
      };
    })
    // Production means a credited deal, nothing else. A row with no units has
    // nothing to show in any column, and command-center.jsx feeds these ids
    // straight into agentFilterOptions — so an empty row would push deleted,
    // disabled and demo accounts into the agent filter dropdown.
    .filter((row) => row.units > 0)
    .sort((a, b) => b.gci - a.gci);
}

// ── Goals & pacing ───────────────────────────────────────────────────────────
export function goalForUser(user, year, field = "amount") {
  const goals = user?.yearlyGoals || [];
  const match = goals.find((g) => Number(g.year) === Number(year));
  return match ? num(match[field]) : 0;
}

export function teamGoal(users, year, field = "amount") {
  return (users || []).reduce((total, u) => total + goalForUser(u, year, field), 0);
}

// Compares actual-to-date against where a straight-line pace would put you today,
// and annualizes the current run-rate into a year-end projection.
export function pace(actual, goal, now = new Date()) {
  if (!goal || goal <= 0) {
    return { hasGoal: false, pct: 0, projected: actual };
  }
  const y = now.getFullYear();
  const start = new Date(y, 0, 1);
  const yearEnd = new Date(y, 11, 31);
  const totalDays = Math.round((yearEnd - start) / 86400000) + 1;
  const elapsed = Math.min(
    totalDays,
    Math.floor((now - start) / 86400000) + 1,
  );
  const expected = goal * (elapsed / totalDays);
  const projected = elapsed > 0 ? actual * (totalDays / elapsed) : actual;
  return {
    hasGoal: true,
    pct: (actual / goal) * 100,
    onPacePct: (elapsed / totalDays) * 100,
    expected,
    projected,
    variance: actual - expected,
    ahead: actual >= expected,
  };
}

// Rounds a chart's top value UP to a clean number (1, 2, 5 or 10 x a power of
// ten) so gridlines land on readable figures like 50,000 / 100,000 instead of
// whatever the tallest bar happened to be.
export function niceCeiling(value) {
  const v = Number(value) || 0;
  if (v <= 0) return 0;
  const base = Math.pow(10, Math.floor(Math.log10(v)));
  const step = [1, 2, 5, 10].find((m) => v / base <= m) || 10;
  return step * base;
}
